import { InputNumber, Select } from 'antd';
import { strings } from '../../lang';
import { determineLeadSalesChannel } from '../../utils/lead-queue-utils';
import { useEffect, useMemo, useRef } from 'react';
import { LoadCalendarButton } from './calendar-button';
import { SalesChannel } from '../../dto/model';
import { useQualifyingAnswersState } from '../../store/qualifying-answers-state';
import { useHeaderState } from '../../store/header-state';
import { useShallow } from 'zustand/shallow';
import { FlowQuestion, QualifyingFlowOption, QualifyingFlowQuestion } from '../../dto/qualifying-flow-models';

type Props = {
  questions: FlowQuestion[];
  buttonStyle?: string;
  loadCalendar: (salesChannel: SalesChannel) => void;
};

export const QualifyingQuestions: React.FC<Props> = ({ questions, buttonStyle, loadCalendar }) => {
  const [answers, setAnswer, clearAnswers] = useQualifyingAnswersState(useShallow((state) => [state.answers, state.setAnswer, state.clearAnswers]));
  const language = useHeaderState(useShallow((state) => state.language)) || 'en';
  const prevLanguageRef = useRef(language);

  // clear answers when language changes
  useEffect(() => {
    if (prevLanguageRef.current !== language) {
      clearAnswers();
      prevLanguageRef.current = language;
    }
  }, [language, clearAnswers]);
  console.log(questions);

  const areAllQuestionsAnswered = useMemo(() => {
    return questions.every((question) => {
      const answer = answers[question.question.id];
      if (!answer) {
        // Check if NumberField allows empty
        if (question.question.questionType === 'NumberField') {
          return question.question.allowEmpty ?? false;
        }
        return false;
      }
      return true;
    });
  }, [answers, questions]);

  const handleClick = () => {
    const leadSalesChannel = determineLeadSalesChannel(questions, answers, language);
    loadCalendar(leadSalesChannel);
  };

  const getLocalizedText = (textObj: { [lang: string]: string }): string => {
    if (!textObj) return '';
    return textObj[language] || textObj['en'] || Object.values(textObj)[0] || '';
  };

  return (
    <section className="bp-section">
      <p style={{ marginBottom: '16px', fontWeight: 500 }}>{strings.leadQueueQualification.instructions}</p>

      {questions.map((question) => {
        const { questionText, options, id, questionType } = question.question;
        const localizedQuestionText = getLocalizedText(questionText);

        return (
          <div key={id} style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '8px' }}>{localizedQuestionText}</div>

            {questionType === 'Dropdown' && (
              <Select style={{ minWidth: '250px' }} value={answers[id]} onChange={(value) => setAnswer(id, value)}>
                {options.map((option) => {
                  const optionText = getLocalizedText(option.text);
                  console.log('Rendering option', { optionKey: option, optionText });
                  return (
                    <Select.Option key={optionText} value={optionText}>
                      {optionText}
                    </Select.Option>
                  );
                })}
              </Select>
            )}

            {questionType === 'NumberField' && (
              <InputNumber
                style={{ minWidth: '250px' }}
                value={answers[id] ? Number.parseFloat(answers[id]) : undefined}
                onChange={(value) => setAnswer(id, value?.toString() ?? '')}
                placeholder={question.question.allowEmpty ? '' : undefined}
                min={0}
                onKeyDown={(e) => {
                  // only allow numbers and control keys
                  const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', '.', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                  if (allowedKeys.includes(e.key) || /^\d$/.test(e.key)) return;
                  e.preventDefault();
                }}
              />
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LoadCalendarButton disabled={!areAllQuestionsAnswered} buttonStyle={buttonStyle} onClick={handleClick} />
      </div>
    </section>
  );
};